import { Temporal } from 'temporal-polyfill';
import type { ISODate, ISOInstant, PlainDateTimeStr, Timing } from './model.ts';

export { Temporal };

export const DEFAULT_TIME_ZONE = 'Europe/Stockholm';

/** Injicerbar klocka – all domänlogik tar tid härifrån, aldrig Date.now() direkt. */
export interface Clock {
  now(): Temporal.Instant;
}
export const systemClock: Clock = { now: () => Temporal.Now.instant() };
export function fixedClock(iso: ISOInstant): Clock & { set(iso: ISOInstant): void; advance(d: Temporal.DurationLike): void } {
  let cur = Temporal.Instant.from(iso);
  return {
    now: () => cur,
    set: (v) => (cur = Temporal.Instant.from(v)),
    advance: (d) => (cur = cur.add(d)),
  };
}

export function isValidTimeZone(tz: string): boolean {
  if (!tz || tz.length > 64) return false;
  try {
    Temporal.Now.instant().toZonedDateTimeISO(tz);
    return true;
  } catch {
    return false;
  }
}

/** Föreslagen zon vid första start: Europe/Stockholm (användaren bekräftar). */
export function suggestTimeZone(): string {
  return DEFAULT_TIME_ZONE;
}

export function instantToIso(i: Temporal.Instant): ISOInstant {
  return i.toString({ smallestUnit: 'second' });
}

export function todayIn(tz: string, now: Temporal.Instant): Temporal.PlainDate {
  return now.toZonedDateTimeISO(tz).toPlainDate();
}

export function plainDate(d: ISODate): Temporal.PlainDate {
  return Temporal.PlainDate.from(d);
}

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const DAY_MS = 86_400_000;

/** Kalenderdag → dagnummer sedan epok (proleptisk gregoriansk, ingen tidszon inblandad). */
function dayNumber(d: ISODate): number {
  const m = ISO_DATE.exec(d);
  if (!m) return plainDate(d).toZonedDateTime('UTC').epochMilliseconds / DAY_MS;
  const dt = new Date(0);
  dt.setUTCFullYear(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Math.round(dt.getTime() / DAY_MS);
}
function fromDayNumber(n: number): ISODate {
  const dt = new Date(n * DAY_MS);
  const p = (v: number, w = 2) => String(v).padStart(w, '0');
  return `${p(dt.getUTCFullYear(), 4)}-${p(dt.getUTCMonth() + 1)}-${p(dt.getUTCDate())}`;
}

/** Snabb dagaritmetik på ISO-datum (används i heta loopar; samma resultat som Temporal.PlainDate). */
export function addDays(d: ISODate, n: number): ISODate {
  return fromDayNumber(dayNumber(d) + n);
}

export function daysBetween(a: ISODate, b: ISODate): number {
  return Math.round(dayNumber(b) - dayNumber(a));
}

/** ISO-datum ÅÅÅÅ-MM-DD jämförs korrekt som strängar. */
export function compareDates(a: ISODate, b: ISODate): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

export class NonexistentLocalTimeError extends Error {
  readonly local: PlainDateTimeStr;
  readonly timeZone: string;
  constructor(local: PlainDateTimeStr, timeZone: string) {
    super(`Klockslaget ${local.replace('T', ' ')} finns inte i ${timeZone} (sommartidsomställning).`);
    this.name = 'NonexistentLocalTimeError';
    this.local = local;
    this.timeZone = timeZone;
  }
}

/**
 * Lokal väggtid → ögonblick.
 * - 'reject': kastar NonexistentLocalTimeError för tider i sommartidsglappet (används när användaren
 *   skriver in en tid – UI måste be om ett explicit val).
 * - 'compatible': RFC 5545-beteende för återkomster och flyttar; tid i glappet flyttas fram med glappets
 *   längd (02:30 → 03:30), dubbeltid väljer första förekomsten. Resultatet flaggas som justerat.
 */
const resolveCache = new Map<string, { zdt: Temporal.ZonedDateTime; adjusted: boolean }>();
const RESOLVE_CACHE_MAX = 50_000;

export function resolveLocal(
  local: PlainDateTimeStr,
  tz: string,
  mode: 'reject' | 'compatible',
): { zdt: Temporal.ZonedDateTime; adjusted: boolean } {
  // Ren funktion av (väggtid, zon) → memoiseras (Temporal-polyfillens zonomvandling är dyr).
  const key = local + '|' + tz;
  const hit = resolveCache.get(key);
  if (hit) {
    if (hit.adjusted && mode === 'reject') throw new NonexistentLocalTimeError(local, tz);
    return hit;
  }
  const res = resolveLocalUncached(local, tz);
  if (resolveCache.size >= RESOLVE_CACHE_MAX) resolveCache.clear();
  resolveCache.set(key, res);
  if (res.adjusted && mode === 'reject') throw new NonexistentLocalTimeError(local, tz);
  return res;
}

function resolveLocalUncached(local: PlainDateTimeStr, tz: string): { zdt: Temporal.ZonedDateTime; adjusted: boolean } {
  const pdt = Temporal.PlainDateTime.from(local);
  const compatible = pdt.toZonedDateTime(tz, { disambiguation: 'compatible' });
  const adjusted = Temporal.PlainDateTime.compare(compatible.toPlainDateTime(), pdt) !== 0;
  return { zdt: compatible, adjusted };
}

export function isAmbiguousLocal(local: PlainDateTimeStr, tz: string): boolean {
  const pdt = Temporal.PlainDateTime.from(local);
  const a = pdt.toZonedDateTime(tz, { disambiguation: 'earlier' });
  const b = pdt.toZonedDateTime(tz, { disambiguation: 'later' });
  return !a.equals(b);
}

/** Start/slut som ögonblick. Heldagar tolkas i visningszonen (de är datum, inte ögonblick). */
export function timingInstants(t: Timing, displayTz: string): { start: Temporal.Instant; end: Temporal.Instant } {
  if (t.kind === 'allDay') {
    return {
      start: plainDate(t.start).toZonedDateTime({ timeZone: displayTz }).toInstant(),
      end: plainDate(t.endExclusive).toZonedDateTime({ timeZone: displayTz }).toInstant(),
    };
  }
  return {
    start: resolveLocal(t.start, t.timeZone, 'compatible').zdt.toInstant(),
    end: resolveLocal(t.end, t.timeZone, 'compatible').zdt.toInstant(),
  };
}

/**
 * Vilka kalenderdagar (i visningszonen) en händelse syns på.
 * Heldag: start ≤ dag < endExclusive. DTEND 2026-10-13 för start 2026-10-12 → bara den 12:e,
 * oavsett visningszon (heldagar är datum, inte ögonblick).
 * Tidsatt: varje dag som intervallet [start, end) berör i visningszonen.
 */
export function daysCovered(t: Timing, displayTz: string): ISODate[] {
  if (t.kind === 'allDay') {
    const out: ISODate[] = [];
    let d = plainDate(t.start);
    const end = plainDate(t.endExclusive);
    if (Temporal.PlainDate.compare(end, d) <= 0) return [t.start];
    while (Temporal.PlainDate.compare(d, end) < 0 && out.length < 366) {
      out.push(d.toString());
      d = d.add({ days: 1 });
    }
    return out;
  }
  const { start, end } = timingInstants(t, displayTz);
  const s = start.toZonedDateTimeISO(displayTz);
  const e = end.toZonedDateTimeISO(displayTz);
  const out: ISODate[] = [s.toPlainDate().toString()];
  let d = s.toPlainDate().add({ days: 1 });
  // Ett slut exakt vid midnatt räknas inte som nästa dag.
  while (Temporal.ZonedDateTime.compare(d.toZonedDateTime({ timeZone: displayTz }), e) < 0 && out.length < 366) {
    out.push(d.toString());
    d = d.add({ days: 1 });
  }
  return out;
}

/** Startdag (i visningszonen) – används för att kopiera en flerdagshändelse exakt en gång. */
export function startDay(t: Timing, displayTz: string): ISODate {
  if (t.kind === 'allDay') return t.start;
  return timingInstants(t, displayTz).start.toZonedDateTimeISO(displayTz).toPlainDate().toString();
}

export function isoWeek(d: ISODate): { week: number; year: number } {
  const p = plainDate(d);
  return { week: p.weekOfYear ?? 0, year: p.yearOfWeek ?? p.year };
}

/** Måndag i ISO-veckan för datumet. */
export function startOfIsoWeek(d: ISODate): ISODate {
  const p = plainDate(d);
  return p.subtract({ days: p.dayOfWeek - 1 }).toString();
}

const pad = (n: number) => String(n).padStart(2, '0');

export function formatTime24(i: Temporal.Instant, tz: string): string {
  const z = i.toZonedDateTimeISO(tz);
  return `${pad(z.hour)}.${pad(z.minute)}`;
}

const MONTHS = ['januari', 'februari', 'mars', 'april', 'maj', 'juni', 'juli', 'augusti', 'september', 'oktober', 'november', 'december'];
const MONTHS_SHORT = ['jan', 'feb', 'mars', 'apr', 'maj', 'juni', 'juli', 'aug', 'sep', 'okt', 'nov', 'dec'];
const WEEKDAYS = ['måndag', 'tisdag', 'onsdag', 'torsdag', 'fredag', 'lördag', 'söndag'];
const WEEKDAYS_SHORT = ['mån', 'tis', 'ons', 'tors', 'fre', 'lör', 'sön'];

export function monthName(m: number): string {
  return MONTHS[m - 1] ?? '';
}
export function weekdayName(dow: number, short = false): string {
  return (short ? WEEKDAYS_SHORT : WEEKDAYS)[dow - 1] ?? '';
}

/** "måndag 12 oktober 2026" */
export function formatDateLong(d: ISODate, withYear = true): string {
  const p = plainDate(d);
  return `${weekdayName(p.dayOfWeek)} ${p.day} ${monthName(p.month)}${withYear ? ' ' + p.year : ''}`;
}
/** "12 okt" */
export function formatDateShort(d: ISODate): string {
  const p = plainDate(d);
  return `${p.day} ${MONTHS_SHORT[p.month - 1]}`;
}

/** "2026-10-11 09.00 (Europe/Stockholm)" */
export function formatInstantSv(i: ISOInstant, tz: string): string {
  const z = Temporal.Instant.from(i).toZonedDateTimeISO(tz);
  return `${z.toPlainDate().toString()} ${pad(z.hour)}.${pad(z.minute)}`;
}

export function hhmmToMinutes(s: string): number {
  const m = /^(\d{1,2}):(\d{2})$/.exec(s);
  if (!m) throw new Error('Ogiltig tid ' + s);
  return Number(m[1]) * 60 + Number(m[2]);
}
